import React, { useState, useContext, useRef, useEffect } from "react";
import CFTag from "../shared/CFTag";
import { VariantContextData } from "./VariantBuilderContext";
import useRemix from "../remix/shared/useRemix";
import froalaExtend from "packs/ctas/froala_extend.js";
import { froalaKey } from "src/froala_license";
import canUseAiRemixText, {
  remixAiUpgradePlan,
} from "../../utils/canUseAiRemixText";
import BillableActionsMeter from "./ai/BillableActionsMeter";
import {
  billableActionsFromResponse,
  dispatchBillableActionsUsed,
} from "../../utils/billableActionsMeter";
import { syncCanvasElementPreview } from "../../utils/builderUtils";

const SuggestionListItem = ({ suggestion, onSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDropdowns = suggestion.dropdowns && suggestion.dropdowns.length > 0;

  const handleClick = () => {
    if (hasDropdowns) {
      setIsExpanded(!isExpanded);
    } else {
      onSelect(suggestion.label);
    }
  };

  return (
    <React.Fragment>
      <div
        key={suggestion.id}
        className="group/context-menu flex items-center justify-between px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg cursor-pointer"
        onClick={handleClick}
      >
        <div className="flex items-center">
          <div className="flex-shrink-0 w-6 flex justify-center">
            {suggestion.icon}
          </div>
          <span className="font-medium text-slate-500 dark:text-slate-400 group-hover/context-menu:text-black dark:group-hover/context-menu:text-white ml-3">
            {suggestion.label}
          </span>
        </div>

        {hasDropdowns && (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              flexShrink: 0,
              marginLeft: "auto",
              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="#003FFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>

      {hasDropdowns && isExpanded && (
        <div className="left-0 top-full mt-2 w-full bg-white dark:bg-gray-800 rounded-lg text-base">
          {suggestion.dropdowns.map((dropdown) => (
            <div
              key={dropdown.id}
              className="pl-[35px] py-2 text-left hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer text-slate-500 dark:text-slate-400 hover:text-black dark:hover:text-white"
              onClick={() => {
                if (dropdown.action) {
                  dropdown.action();
                } else {
                  onSelect(dropdown.value ? dropdown.value : dropdown.label);
                }

                setIsExpanded(false);
              }}
            >
              {dropdown.label}
            </div>
          ))}
        </div>
      )}
    </React.Fragment>
  );
};

const VariantBuilderSettingRemixContent = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const website = contextData.objects.website;
  const account = contextData.objects.account;
  const cta = contextData.objects.cta;
  const variant = contextData.objects.variant;
  const current_user = contextData.objects.current_user;
  const contentValue = props.value;

  const canUseRemix = true;
  const stylesheet = style.stylesheet(props.setting_name, canUseRemix);

  const accountPlanType = contextData?.objects?.subscription?.plan_type;
  const websitePlanType = website?.plan_type;
  const is2024AccountPlan = String(accountPlanType || "").indexOf("2024") > -1;
  const is2024WebsitePlan = String(websitePlanType || "").indexOf("2024") > -1;

  // Remix text editor: available on free (with remaining actions) and paid 2025/2026.
  const canUseRemixAI = canUseAiRemixText({
    currentUser: current_user,
    website,
    subscription: contextData?.objects?.subscription,
    account,
  });

  if (!canUseRemix) {
    return null;
  }

  const { Remix } = useRemix({ website_id: website.id });

  const newTones = [];

  // Add brand tone if present
  if (website?.data?.brand_tone) {
    newTones.push({
      id: "brand_tone",
      label: "Brand",
      value: website.data.brand_tone,
      active: true,
    });
  }

  // Add CTA remix tone if present
  if (cta?.options?.remix_tone) {
    newTones.push({
      id: "cta_remix_tone",
      label: "Campaign",
      value: cta.options.remix_tone,
      active: true,
    });
  }

  // Add variant remix tone if present
  if (variant?.options?.remix_tone) {
    newTones.push({
      id: "variant_remix_tone",
      label: "Variant",
      value: variant.options.remix_tone,
      active: true,
    });
  }

  const [isExpanded, setIsExpanded] = useState(false);
  const [isRemixing, setIsRemixing] = useState(false);
  const [tones, setTones] = useState(newTones);
  const [prompt, setPrompt] = useState("");
  const [promptResponse, setPromptResponse] = useState("");

  const toggleExpanded = (clickEvent) => {
    // Check if website can use Remix AI before allowing expansion
    if (!canUseRemixAI) {
      const exhausted =
        Number(account?.billable_actions_limit) > 0 &&
        Number(account?.billable_actions_usage) >=
          Number(account?.billable_actions_limit);

      if (exhausted) {
        const limit = Number(account.billable_actions_limit);
        const freePlan =
          String(accountPlanType || "").trim() === "free" ||
          String(websitePlanType || "").trim() === "free";
        subscriptions.upgrade_modal(
          clickEvent,
          false,
          freePlan
            ? `continue with Remix AI after using all ${limit} included AI actions.`
            : `continue with Remix AI after using all ${limit} actions this month.`,
          remixAiUpgradePlan({
            subscription: contextData?.objects?.subscription,
            website,
          }),
          "Triggered feature CTA – Remix AI actions limit",
          false,
          "x3rs6cvrwi"
        );
        return;
      }

      let requiredPlan = remixAiUpgradePlan({
        subscription: contextData?.objects?.subscription,
        website,
      });

      if (
        String(accountPlanType || "").includes("2024") ||
        String(websitePlanType || "").includes("2024")
      ) {
        requiredPlan = "remix";
      }

      subscriptions.upgrade_modal(
        clickEvent,
        false,
        "use remix AI for copywriting",
        requiredPlan,
        "Triggered feature CTA – Remix AI",
        false,
        "x3rs6cvrwi"
      );
      return;
    }
    setIsExpanded(!isExpanded);
  };

  const disableTone = (toneId) => {
    setTones((prevTones) =>
      prevTones.map((tone) =>
        tone.id === toneId ? { ...tone, active: false } : tone
      )
    );
  };

  const enableTone = (toneId) => {
    setTones((prevTones) =>
      prevTones.map((tone) =>
        tone.id === toneId ? { ...tone, active: true } : tone
      )
    );
  };

  const appendToPrompt = (value) => {
    let newPrompt = prompt;
    if (prompt.includes(value)) {
      return;
    }

    let isTonePrompt = value.toLowerCase().includes("tone");
    if (isTonePrompt && prompt.toLowerCase().includes("tone")) {
      return;
    }

    if (newPrompt) {
      newPrompt += ", " + value;
    } else {
      newPrompt = value;
    }

    setPrompt(newPrompt);
  };

  const handleRemix = async () => {
    if (isRemixing) return;

    let payloadPrompt = `
      You are given a piece of content and a list of instructions.
      You need to rewrite the content to follow the instructions.
      The content is: ${contentValue}.
      The instructions are: ${prompt}.

      If the instructions do not include making it longer or shorter, keep it the same length.

      The voice tone should follow these instructions: ${tones
        .filter((tone) => tone.active)
        .map((tone) => tone.value)
        .join(", ")}

      Return the content only respecting existent HTML tags. Don't include any Notes nor anything else.
    `;

    try {
      setIsRemixing(true);
      const response = await Remix.promptContent(payloadPrompt);

      if (response && response.content) {
        dispatchBillableActionsUsed(billableActionsFromResponse(response));
        setPromptResponse(response.content);
      }
    } catch (error) {
      console.error("Remix prompt failed", error);
    } finally {
      setIsRemixing(false);
    }
  };

  const handleRedo = () => {
    setPromptResponse("");
  };

  const handleDiscard = () => {
    setPromptResponse("");
    setPrompt("");
  };

  const syncLivePreview = (html) => {
    if (props.editor_live_preview === false) return;
    syncCanvasElementPreview(props.object_id, props.preview_selector, html);
  };

  const syncSettingsEditor = (html) => {
    if (!props.setting_name || !props.object_type || !html) {
      return;
    }

    const selector = `.editor[data-setting_name="${props.setting_name}"][data-object_type="${props.object_type}"]${
      props.object_id ? `[data-object_id="${props.object_id}"]` : ""
    }`;
    const editorEl = document.querySelector(selector);
    if (!editorEl) {
      return;
    }

    // Update through the live Froala instance's API so its internal model, undo
    // stack, and change tracking stay in sync; writing innerHTML directly leaves
    // Froala holding the pre-Replace content, so a later keystroke or undo
    // reverts the applied copy.
    const instance = (FroalaEditor.INSTANCES || []).find(
      (editor) => editor && editor.el === editorEl
    );
    if (instance && instance.html) {
      instance.html.set(html);
      if (instance.undo) instance.undo.saveStep();
    } else {
      const froalaView = editorEl.querySelector(".fr-element");
      (froalaView || editorEl).innerHTML = html;
    }
  };

  const handleReplace = () => {
    const html = promptResponse;
    if (!html) {
      return;
    }

    if (builder && props?.object_type && props?.object_id) {
      builder.update([
        {
          object_type: props.object_type,
          object_id: props.object_id,
          setting_name: props.setting_name,
          value: html,
        },
      ]);
    }

    // builder.update mutates nested objects in place; Section's React.memo
    // deep-equality check often skips re-render, so mirror the settings
    // editor livePreview path and patch the canvas/settings DOM directly.
    syncLivePreview(html);
    syncSettingsEditor(html);

    handleDiscard();
    setIsExpanded(false);
  };

  const TRANSLATION_LANGUAGES = [
    { id: "arabic", label: "Arabic" },
    { id: "chinese", label: "Chinese" },
    { id: "english_us", label: "English (US)" },
    { id: "english_uk", label: "English (UK)" },
    { id: "french", label: "French" },
    { id: "german", label: "German" },
    { id: "italian", label: "Italian" },
    { id: "spanish", label: "Spanish" },
    { id: "portuguese", label: "Portuguese" },
  ];

  const SUGGESTIONS = [
    {
      id: "rewrite",
      label: "Rewrite",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.65337 10.875C4.41685 13.2704 6.60348 15 9.18182 15C12.3951 15 15 12.3137 15 9C15 5.68629 12.3951 3 9.18182 3C7.02827 3 5.148 4.2066 4.14201 6M3.65337 5.41383L4.45478 6.18756M5.90909 6.75H3V3.75L5.90909 6.75Z"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: "rewrite_brand",
      label: "Rewrite in brand voice",
      icon: (
        <svg
          width="22"
          height="19"
          viewBox="0 0 22 19"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.3746 4.43421L19.3252 4.13434C18.8527 4.00111 18.4222 3.74884 18.075 3.40167C17.7279 3.0545 17.4756 2.62405 17.3424 2.15149L17.0425 1.10214C17.0274 1.06543 17.0018 1.03402 16.9688 1.01193C16.9358 0.989827 16.897 0.978027 16.8573 0.978027C16.8176 0.978027 16.7789 0.989827 16.7459 1.01193C16.7129 1.03402 16.6873 1.06543 16.6722 1.10214L16.3723 2.15149C16.2391 2.62405 15.9868 3.0545 15.6396 3.40167C15.2925 3.74884 14.862 4.00111 14.3895 4.13434L13.3401 4.43421C13.2998 4.44564 13.2644 4.46991 13.2391 4.50332C13.2139 4.53674 13.2002 4.57748 13.2002 4.61936C13.2002 4.66125 13.2139 4.70199 13.2391 4.7354C13.2644 4.76882 13.2998 4.79309 13.3401 4.80452L14.3895 5.10439C14.862 5.23762 15.2925 5.48988 15.6396 5.83705C15.9868 6.18423 16.2391 6.61468 16.3723 7.08723L16.6722 8.13659C16.6836 8.17688 16.7079 8.21234 16.7413 8.23759C16.7747 8.26284 16.8155 8.2765 16.8573 8.2765C16.8992 8.2765 16.94 8.26284 16.9734 8.23759C17.0068 8.21234 17.0311 8.17688 17.0425 8.13659L17.3424 7.08723C17.4756 6.61468 17.7279 6.18423 18.075 5.83705C18.4222 5.48988 18.8527 5.23762 19.3252 5.10439L20.3746 4.80452C20.4149 4.79309 20.4503 4.76882 20.4756 4.7354C20.5008 4.70199 20.5145 4.66125 20.5145 4.61936C20.5145 4.57748 20.5008 4.53674 20.4756 4.50332C20.4503 4.46991 20.4149 4.44564 20.3746 4.43421Z"
            fill="#003FFF"
          />
          <path
            d="M9.75131 8.40584L12.594 11.2485M3 17.9998H5.84266L13.3046 10.5378C13.4913 10.3512 13.6393 10.1296 13.7404 9.88572C13.8414 9.64185 13.8934 9.38047 13.8934 9.11651C13.8934 8.85254 13.8414 8.59116 13.7404 8.34729C13.6393 8.10342 13.4913 7.88183 13.3046 7.69518C13.118 7.50853 12.8964 7.36047 12.6525 7.25945C12.4086 7.15844 12.1473 7.10645 11.8833 7.10645C11.6193 7.10645 11.358 7.15844 11.1141 7.25945C10.8702 7.36047 10.6486 7.50853 10.462 7.69518L3 15.1572V17.9998Z"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: "shorter",
      label: "Make shorter",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.875 11H3M15 6.5H3"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      id: "longer",
      label: "Make longer",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 13.5H3M10.875 9H3M15 4.5H3"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      id: "spelling",
      label: "Fix spelling & grammar",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M13.8667 5L6.50827 12.4L4 9.87754"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: "improve",
      label: "Improve writing",
      icon: (
        <svg
          width="19"
          height="18"
          viewBox="0 0 19 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.81445 6.92304V5.53842C2.81445 5.1712 2.96273 4.81902 3.22667 4.55935C3.49061 4.29969 3.84859 4.15381 4.22186 4.15381H5.62927M2.81445 12.4615V13.8461C2.81445 14.2133 2.96273 14.5655 3.22667 14.8252C3.49061 15.0849 3.84859 15.2307 4.22186 15.2307H5.62927M11.2589 15.2307H12.6663C13.0396 15.2307 13.3976 15.0849 13.6615 14.8252C13.9254 14.5655 14.0737 14.2133 14.0737 13.8461V12.4615M8.44408 12.4615V7.61535M6.33297 7.61535H10.5552"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <g clipPath="url(#clip0_3604_42411)">
            <path
              d="M17.9088 4.31954L16.8697 4.02742C16.4018 3.89763 15.9755 3.65188 15.6318 3.31368C15.288 2.97548 15.0382 2.55614 14.9063 2.09579L14.6094 1.07354C14.5944 1.03778 14.569 1.00719 14.5364 0.985659C14.5037 0.964131 14.4653 0.952637 14.426 0.952637C14.3867 0.952637 14.3483 0.964131 14.3156 0.985659C14.283 1.00719 14.2576 1.03778 14.2427 1.07354L13.9457 2.09579C13.8138 2.55614 13.564 2.97548 13.2202 3.31368C12.8765 3.65188 12.4502 3.89763 11.9823 4.02742L10.9432 4.31954C10.9033 4.33068 10.8682 4.35432 10.8432 4.38688C10.8182 4.41943 10.8047 4.45912 10.8047 4.49992C10.8047 4.54072 10.8182 4.58041 10.8432 4.61296C10.8682 4.64551 10.9033 4.66915 10.9432 4.68029L11.9823 4.97242C12.4502 5.1022 12.8765 5.34795 13.2202 5.68616C13.564 6.02436 13.8138 6.4437 13.9457 6.90404L14.2427 7.92629C14.254 7.96554 14.278 8.00009 14.3111 8.02469C14.3442 8.04929 14.3845 8.0626 14.426 8.0626C14.4675 8.0626 14.5078 8.04929 14.5409 8.02469C14.574 8.00009 14.598 7.96554 14.6094 7.92629L14.9063 6.90404C15.0382 6.4437 15.288 6.02436 15.6318 5.68616C15.9755 5.34795 16.4018 5.1022 16.8697 4.97242L17.9088 4.68029C17.9487 4.66915 17.9838 4.64551 18.0088 4.61296C18.0338 4.58041 18.0473 4.54072 18.0473 4.49992C18.0473 4.45912 18.0338 4.41943 18.0088 4.38688C17.9838 4.35432 17.9487 4.33068 17.9088 4.31954Z"
              fill="#003FFF"
            />
          </g>
          <defs>
            <clipPath id="clip0_3604_42411">
              <rect
                width="9.14815"
                height="9"
                fill="white"
                transform="translate(9.85156)"
              />
            </clipPath>
          </defs>
        </svg>
      ),
    },
    {
      id: "tone",
      label: "Change tone",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 11.25V6.75M9 15V3M6 12.75V5.25M15 6.75V11.25M12 5.25L12 12.75"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      dropdowns: [
        {
          id: "brand",
          label: "Use Brand Tone",
          action: () => {
            enableTone("brand_tone");
            enableTone("cta_remix_tone");
            enableTone("variant_remix_tone");
          },
        },
        {
          id: "professional",
          label: "Professional Tone",
        },
        {
          id: "casual",
          label: "Casual Tone",
        },
        {
          id: "playful",
          label: "Playful Tone",
        },
        {
          id: "witty",
          label: "Witty Tone",
        },
        {
          id: "straightforward",
          label: "Straightforward Tone",
        },
        {
          id: "confident",
          label: "Confident Tone",
        },
        {
          id: "friendly",
          label: "Friendly Tone",
        },
        {
          id: "sassy",
          label: "Sassy Tone",
        },
        {
          id: "edgy",
          label: "Edgy Tone",
        },
        {
          id: "sophisticated",
          label: "Sophisticated Tone",
        },
        {
          id: "urgent",
          label: "Urgent Tone",
        },
        {
          id: "storytelling",
          label: "Storytelling Tone",
        },
        {
          id: "relatable",
          label: "Relatable Tone",
        },
        {
          id: "conversion_focused",
          label: "Conversion-focused Tone",
        },
        {
          id: "social_proof_driven",
          label: "Social Proof-driven Tone",
        },
        {
          id: "seasonal_festive",
          label: "Seasonal & Festive Tone",
          value: "Seasonal & Festive Tone for",
        },
        {
          id: "mysterious_teaser",
          label: "Mysterious/Teaser Tone",
        },
        {
          id: "custom",
          label: "Custom",
          value: "Change tone to",
        },
      ],
    },
    {
      id: "translate",
      label: "Translate to language",
      icon: (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_3604_42437)">
            <path
              d="M16.9266 4.31954L15.9043 4.02742C15.444 3.89763 15.0246 3.65188 14.6864 3.31368C14.3482 2.97548 14.1025 2.55614 13.9727 2.09579L13.6806 1.07354C13.6659 1.03778 13.6409 1.00719 13.6088 0.985659C13.5766 0.964131 13.5388 0.952637 13.5002 0.952637C13.4615 0.952637 13.4237 0.964131 13.3916 0.985659C13.3595 1.00719 13.3345 1.03778 13.3198 1.07354L13.0277 2.09579C12.8979 2.55614 12.6521 2.97548 12.3139 3.31368C11.9757 3.65188 11.5564 3.89763 11.0961 4.02742L10.0738 4.31954C10.0346 4.33068 10 4.35432 9.97541 4.38688C9.95081 4.41943 9.9375 4.45912 9.9375 4.49992C9.9375 4.54072 9.95081 4.58041 9.97541 4.61296C10 4.64551 10.0346 4.66915 10.0738 4.68029L11.0961 4.97242C11.5564 5.1022 11.9757 5.34795 12.3139 5.68616C12.6521 6.02436 12.8979 6.4437 13.0277 6.90404L13.3198 7.92629C13.3309 7.96554 13.3546 8.00009 13.3871 8.02469C13.4197 8.04929 13.4594 8.0626 13.5002 8.0626C13.541 8.0626 13.5807 8.04929 13.6132 8.02469C13.6458 8.00009 13.6694 7.96554 13.6806 7.92629L13.9727 6.90404C14.1025 6.4437 14.3482 6.02436 14.6864 5.68616C15.0246 5.34795 15.444 5.1022 15.9043 4.97242L16.9266 4.68029C16.9658 4.66915 17.0003 4.64551 17.0249 4.61296C17.0495 4.58041 17.0629 4.54072 17.0629 4.49992C17.0629 4.45912 17.0495 4.41943 17.0249 4.38688C17.0003 4.35432 16.9658 4.33068 16.9266 4.31954Z"
              fill="#003FFF"
            />
          </g>
          <path
            d="M1.38477 6.23081H6.23092M4.8463 4.84619V6.23081C4.8463 9.28942 3.29623 11.7693 1.38477 11.7693M2.07707 9.00004C2.07707 10.4843 4.12077 11.7056 6.71553 11.7693M6.92323 16.6154L9.69246 10.3847L12.4617 16.6154M11.8386 15.2308H7.54632"
            stroke="#003FFF"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <defs>
            <clipPath id="clip0_3604_42437">
              <rect
                width="9"
                height="9"
                fill="white"
                transform="translate(9)"
              />
            </clipPath>
          </defs>
        </svg>
      ),
      dropdowns: [
        ...TRANSLATION_LANGUAGES.map((lang) => ({
          id: lang.id,
          label: lang.label,
          action: () => {
            if (!prompt.includes("Translate")) {
              appendToPrompt(`Translate to ${lang.label}`);
            }
          },
        })),
        {
          id: "custom",
          label: "Custom",
          action: () => {
            const language = window.prompt("What is the language?");
            if (!language) return;
            const text = `Translate to ${language.trim()}`;
            appendToPrompt(text);
          },
        },
      ],
    },
  ];

  const promptResponseDiv = useRef(null);
  const FroalaEditor = froalaExtend(
    contextData.objects.website,
    contextData?.objects?.variant?.fields,
    contextData?.objects?.variant?.actions
  );
  let froala;

  useEffect(() => {
    // prettier-ignore
    var editor = promptResponseDiv.current;
    if (editor) {
      editor.innerHTML = promptResponse ? promptResponse : "";
      froala = new FroalaEditor(editor, {
        attribution: false,
        toolbarBottom: true,
        toolbarButtons: [],
        toolbarButtonsMD: [],
        toolbarButtonsSM: [],
        toolbarButtonsXS: [],
        toolbarInline: true,
        toolbarVisibleWithoutSelection: false,
        quickInsertEnabled: false,
        key: froalaKey(),
        events: {
          focus: function () {
            editor.classList.add("border-2", "border-[#003FFF]");
          },
          blur: function () {
            froala.html.cleanEmptyTags();
            let html = froala.html.get(true);
            setPromptResponse(html);

            editor.classList.remove("border-2", "border-[#003FFF]");
          },
        },
      });
    }
  }, [promptResponse]);

  if (website?.data?.["ai-enabled"] == "false") {
    return null;
  }

  return (
    <React.Fragment>
      <div
        className={`cf-remix-content w-full bg-white dark:bg-gray-900 rounded-lg border border-transparent overflow-hidden ${
          props.input_inline ? "input-inline" : ""
        }`}
        style={{
          background: `
            linear-gradient(white, white) padding-box,
            linear-gradient(90deg, #6B8CFF 0%, #003FFF 100%) border-box
          `,
          border: "1px solid transparent",
        }}
      >
        <button
          onClick={toggleExpanded}
          className="flex items-center gap-2 w-full relative justify-center px-5 py-3 cursor-pointer font-medium text-sm text-[#003FFF] dark:text-blue-400 min-w-[140px] bg-transparent border-none"
        >
          <svg
            width="13"
            height="14"
            viewBox="0 0 13 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.5086 7.86766L8.97152 7.42808C8.27936 7.23279 7.64886 6.86299 7.14034 6.35408C6.63183 5.84516 6.26232 5.21416 6.06718 4.52145L5.62795 2.9832C5.60586 2.92939 5.56829 2.88336 5.52 2.85096C5.47171 2.81857 5.41488 2.80127 5.35675 2.80127C5.29861 2.80127 5.24179 2.81857 5.1935 2.85096C5.14521 2.88336 5.10763 2.92939 5.08554 2.9832L4.64631 4.52145C4.45117 5.21416 4.08166 5.84516 3.57315 6.35408C3.06464 6.86299 2.43413 7.23279 1.74197 7.42808L0.204942 7.86766C0.145925 7.88443 0.0939831 7.92 0.0569971 7.96898C0.0200111 8.01797 0 8.07769 0 8.13908C0 8.20048 0.0200111 8.2602 0.0569971 8.30918C0.0939831 8.35817 0.145925 8.39374 0.204942 8.4105L1.74197 8.85008C2.43413 9.04538 3.06464 9.41517 3.57315 9.92409C4.08166 10.433 4.45117 11.064 4.64631 11.7567L5.08554 13.295C5.10229 13.354 5.13783 13.406 5.18678 13.443C5.23572 13.48 5.2954 13.5001 5.35675 13.5001C5.41809 13.5001 5.47777 13.48 5.52671 13.443C5.57566 13.406 5.6112 13.354 5.62795 13.295L6.06718 11.7567C6.26232 11.064 6.63183 10.433 7.14034 9.92409C7.64886 9.41517 8.27936 9.04538 8.97152 8.85008L10.5086 8.4105C10.5676 8.39374 10.6195 8.35817 10.6565 8.30918C10.6935 8.2602 10.7135 8.20048 10.7135 8.13908C10.7135 8.07769 10.6935 8.01797 10.6565 7.96898C10.6195 7.92 10.5676 7.88443 10.5086 7.86766Z"
              fill="#003FFF"
            />
            <path
              d="M12.9087 2.75173L12.2256 2.55636C11.918 2.46956 11.6378 2.30521 11.4118 2.07903C11.1858 1.85284 11.0215 1.5724 10.9348 1.26452L10.7396 0.58086C10.7298 0.556941 10.7131 0.536483 10.6916 0.522085C10.6702 0.507687 10.6449 0.5 10.6191 0.5C10.5932 0.5 10.568 0.507687 10.5465 0.522085C10.525 0.536483 10.5083 0.556941 10.4985 0.58086L10.3033 1.26452C10.2166 1.5724 10.0524 1.85284 9.82635 2.07903C9.60034 2.30521 9.32012 2.46956 9.01249 2.55636L8.32937 2.75173C8.30314 2.75918 8.28005 2.77499 8.26361 2.79676C8.24718 2.81853 8.23828 2.84507 8.23828 2.87236C8.23828 2.89965 8.24718 2.92619 8.26361 2.94796C8.28005 2.96973 8.30314 2.98554 8.32937 2.99299L9.01249 3.18836C9.32012 3.27516 9.60034 3.43951 9.82635 3.6657C10.0524 3.89188 10.2166 4.17233 10.3033 4.4802L10.4985 5.16386C10.506 5.19011 10.5218 5.21322 10.5435 5.22967C10.5653 5.24612 10.5918 5.25502 10.6191 5.25502C10.6463 5.25502 10.6728 5.24612 10.6946 5.22967C10.7164 5.21322 10.7321 5.19011 10.7396 5.16386L10.9348 4.4802C11.0215 4.17233 11.1858 3.89188 11.4118 3.6657C11.6378 3.43951 11.918 3.27516 12.2256 3.18836L12.9087 2.99299C12.935 2.98554 12.9581 2.96973 12.9745 2.94796C12.9909 2.92619 12.9998 2.89965 12.9998 2.87236C12.9998 2.84507 12.9909 2.81853 12.9745 2.79676C12.9581 2.77499 12.935 2.75918 12.9087 2.75173Z"
              fill="#003FFF"
            />
          </svg>

          <span className="text-[12px]">Remix with AI</span>

          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              flexShrink: 0,
              marginLeft: "auto",
              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="#003FFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isExpanded && (
          <div className="px-4 pb-0 border-t border-gray-200 dark:border-gray-700 text-xl">
            {tones.filter((tone) => tone.active).length > 0 && (
              <>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-3 mt-3">
                  Tones Included
                </div>

                <div className="flex flex-wrap gap-2 text-sm mb-4">
                  {tones
                    .filter((tone) => tone.active)
                    .map((tone) => (
                      <CFTag
                        key={tone.id}
                        label={tone.label}
                        onRemove={() => disableTone(tone.id)}
                      />
                    ))}
                </div>
              </>
            )}

            <div className="relative">
              {!promptResponse && (
                <>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Ask AI to write..."
                    className="w-full text-[14px] px-3 py-[15px] border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#003FFF] focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                    rows={6}
                  />

                  <button
                    onClick={handleRemix}
                    disabled={isRemixing}
                    aria-busy={isRemixing}
                    className={`border-0 absolute bottom-[12px] right-[7px] px-5 py-3 rounded-lg text-white font-medium flex items-center gap-2 shadow-lg transition-shadow duration-200 ${
                      isRemixing
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:shadow-xl"
                    }`}
                    style={{
                      background:
                        "linear-gradient(90deg, #6B8CFF 0%, #003FFF 100%)",
                      fontFamily: "inherit",
                    }}
                  >
                    {!isRemixing && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 13 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.5086 7.86766L8.97152 7.42808C8.27936 7.23279 7.64886 6.86299 7.14034 6.35408C6.63183 5.84516 6.26232 5.21416 6.06718 4.52145L5.62795 2.9832C5.60586 2.92939 5.56829 2.88336 5.52 2.85096C5.47171 2.81857 5.41488 2.80127 5.35675 2.80127C5.29861 2.80127 5.24179 2.81857 5.1935 2.85096C5.14521 2.88336 5.10763 2.92939 5.08554 2.9832L4.64631 4.52145C4.45117 5.21416 4.08166 5.84516 3.57315 6.35408C3.06464 6.86299 2.43413 7.23279 1.74197 7.42808L0.204942 7.86766C0.145925 7.88443 0.0939831 7.92 0.0569971 7.96898C0.0200111 8.01797 0 8.07769 0 8.13908C0 8.20048 0.0200111 8.2602 0.0569971 8.30918C0.0939831 8.35817 0.145925 8.39374 0.204942 8.4105L1.74197 8.85008C2.43413 9.04538 3.06464 9.41517 3.57315 9.92409C4.08166 10.433 4.45117 11.064 4.64631 11.7567L5.08554 13.295C5.10229 13.354 5.13783 13.406 5.18678 13.443C5.23572 13.48 5.2954 13.5001 5.35675 13.5001C5.41809 13.5001 5.47777 13.48 5.52671 13.443C5.57566 13.406 5.6112 13.354 5.62795 13.295L6.06718 11.7567C6.26232 11.064 6.63183 10.433 7.14034 9.92409C7.64886 9.41517 8.27936 9.04538 8.97152 8.85008L10.5086 8.4105C10.5676 8.39374 10.6195 8.35817 10.6565 8.30918C10.6935 8.2602 10.7135 8.20048 10.7135 8.13908C10.7135 8.07769 10.6935 8.01797 10.6565 7.96898C10.6195 7.92 10.5676 7.88443 10.5086 7.86766Z"
                          fill="white"
                        />
                        <path
                          d="M12.9087 2.75173L12.2256 2.55636C11.918 2.46956 11.6378 2.30521 11.4118 2.07903C11.1858 1.85284 11.0215 1.5724 10.9348 1.26452L10.7396 0.58086C10.7298 0.556941 10.7131 0.536483 10.6916 0.522085C10.6702 0.507687 10.6449 0.5 10.6191 0.5C10.5932 0.5 10.568 0.507687 10.5465 0.522085C10.525 0.536483 10.5083 0.556941 10.4985 0.58086L10.3033 1.26452C10.2166 1.5724 10.0524 1.85284 9.82635 2.07903C9.60034 2.30521 9.32012 2.46956 9.01249 2.55636L8.32937 2.75173C8.30314 2.75918 8.28005 2.77499 8.26361 2.79676C8.24718 2.81853 8.23828 2.84507 8.23828 2.87236C8.23828 2.89965 8.24718 2.92619 8.26361 2.94796C8.28005 2.96973 8.30314 2.98554 8.32937 2.99299L9.01249 3.18836C9.32012 3.27516 9.60034 3.43951 9.82635 3.6657C10.0524 3.89188 10.2166 4.17233 10.3033 4.4802L10.4985 5.16386C10.506 5.19011 10.5218 5.21322 10.5435 5.22967C10.5653 5.24612 10.5918 5.25502 10.6191 5.25502C10.6463 5.25502 10.6728 5.24612 10.6946 5.22967C10.7164 5.21322 10.7321 5.19011 10.7396 5.16386L10.9348 4.4802C11.0215 4.17233 11.1858 3.89188 11.4118 3.6657C11.6378 3.43951 11.918 3.27516 12.2256 3.18836L12.9087 2.99299C12.935 2.98554 12.9581 2.96973 12.9745 2.94796C12.9909 2.92619 12.9998 2.89965 12.9998 2.87236C12.9998 2.84507 12.9909 2.81853 12.9745 2.79676C12.9581 2.77499 12.935 2.75918 12.9087 2.75173Z"
                          fill="white"
                        />
                      </svg>
                    )}
                    {isRemixing ? "Remixing…" : "Remix"}
                  </button>
                </>
              )}

              {promptResponse && (
                <>
                  <div
                    className={
                      "editor border border-solid border-gray-300 dark:border-gray-600 rounded-lg pt-3 pl-3 pr-3 pb-[40px] bg-white dark:bg-gray-800"
                    }
                    ref={promptResponseDiv}
                    dangerouslySetInnerHTML={{ __html: promptResponse }}
                  ></div>

                  <div className="absolute left-[10px] bottom-[10px] flex gap-3">
                    <button
                      onClick={handleRedo}
                      className="border-[1px] border-solid py-2 px-4 rounded-lg text-base cursor-pointer bg-white dark:bg-gray-800 text-black dark:text-white border-slate-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Redo
                    </button>

                    <button
                      onClick={handleDiscard}
                      className="border-[1px] border-solid py-2 px-4 rounded-lg text-base cursor-pointer bg-white dark:bg-gray-800 text-black dark:text-white border-slate-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Discard
                    </button>

                    <button
                      onClick={handleReplace}
                      className="border-[1px] border-solid py-2 px-4 rounded-lg text-base cursor-pointer bg-slate-950 dark:bg-white border-slate-950 dark:border-white text-white dark:text-slate-950 flex gap-2 hover:bg-slate-900 dark:hover:bg-gray-100"
                    >
                      <svg
                        width="13"
                        height="14"
                        viewBox="0 0 13 14"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.8667 3.5L4.50827 10.9L2 8.37754"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Replace
                    </button>
                  </div>
                </>
              )}
            </div>

            {!promptResponse && (
              <>
                <div className="font-medium text-gray-800 dark:text-gray-200 mb-3 mt-3">
                  Suggestions...
                </div>

                <div className="py-2">
                  {SUGGESTIONS.map((suggestion) => (
                    <SuggestionListItem
                      key={suggestion.id}
                      suggestion={suggestion}
                      onSelect={appendToPrompt}
                    />
                  ))}
                </div>
              </>
            )}

            <div className="-mx-4 mt-2">
              <BillableActionsMeter
                usage={account && account.billable_actions_usage}
                limit={account && account.billable_actions_limit}
                accountId={account && account.id}
                websiteId={website && website.id}
              />
            </div>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: stylesheet }} />
    </React.Fragment>
  );
};

const style = {
  stylesheet: (setting_name, canUseRemix) => {
    // prettier-ignore
    let desktopStyles = `
      .setting[data-setting_name="${setting_name}"] .cf-remix-content.input-inline {
        border: 1px solid #E2E8F0 !important;
        background: linear-gradient(90deg, rgba(107, 140, 255, 0.05) 0%, rgba(0, 63, 255, 0.05) 100%) !important;
        border-top: none !important;
        border-top-left-radius: 0px !important;
        border-top-right-radius: 0px !important;
      }

      .dark .setting[data-setting_name="${setting_name}"] .cf-remix-content.input-inline {
        border: 1px solid #374151 !important;
        background: linear-gradient(90deg, rgba(107, 140, 255, 0.1) 0%, rgba(0, 63, 255, 0.1) 100%) !important;
      }

    ${canUseRemix && `
      .setting[data-setting_name="${setting_name}"] .fr-second-toolbar {
        border-radius: 0px !important;
        border-bottom: none !important;
      }
    `}
    `;

    return desktopStyles;
  },
};

export default VariantBuilderSettingRemixContent;
